/* -------------------------------------------------------------------------- */
/* 1. Les registres                                                           */
/* -------------------------------------------------------------------------- */
/** Version du format d'échange. Monte quand un fichier change de forme. */
export const FORMAT_CHAPITRE_VERSION = 1;
/** Les fichiers de la partie « programme » d'un dossier de chapitre. */
export const FICHIERS_PROGRAMME = ['chapitre.json', 'notions.json', 'attentes.json'];
/** `Chapitre.nature` — une chaîne en base, tenue par cette liste. */
export const NATURES_CHAPITRE = ['chapitre', 'preparation_examen', 'revision', 'socle'];
/**
 * LE SOCLE DE PRÉREQUIS PARTAGÉS (capacité `CAP-01`, décision F-63 du
 * fondateur) — un dossier au format d'un chapitre, qui n'est PAS un chapitre
 * de la classe : des notions d'avant le secondaire, avec leur fiche, leurs
 * capsules et leurs exercices, sur lesquelles des chapitres de plusieurs
 * niveaux s'appuient.
 *
 * Trois règles, tenues par le code et non par l'usage :
 *
 *  - un socle n'entre dans AUCUNE table des matières : ni celle de l'élève, ni
 *    les chapitres proposés pour un objectif, ni la position face au programme
 *    (`chapitreDeTableDesMatieres`) ;
 *  - il se joint par ses NOTIONS, depuis les chapitres qui s'y appuient : la
 *    liste « ce chapitre s'appuie sur » (`PrerequisNotionDto.ressources`) et le
 *    remède d'une erreur dont le prérequis est en cause
 *    (`RemedeSurPlaceDto.appui`) ;
 *  - son `niveau` n'est qu'un RATTACHEMENT TECHNIQUE (le premier niveau qui
 *    s'y appuie) : il n'est le niveau d'aucun élève, et aucun écran ne
 *    l'affiche. `NiveauScolaire` ne gagne aucune valeur.
 */
export const NATURE_SOCLE = 'socle';
/** Ce chapitre est-il un socle de prérequis partagés ? `nature` est une chaîne en base. */
export function chapitreEstUnSocle(chapitre) {
    return chapitre.nature === NATURE_SOCLE;
}
/** Ce chapitre a-t-il sa place dans une table des matières ? Tout, sauf un socle. */
export function chapitreDeTableDesMatieres(chapitre) {
    return !chapitreEstUnSocle(chapitre);
}
/**
 * Le dossier des socles d'une matière : `contenu/<matière>/prerequis/<dossier>/`.
 * Ni « p6 » ni « primaire » dans le chemin : un socle n'est pas un niveau.
 */
export const DOSSIER_SOCLES = 'prerequis';
/**
 * `AttenteNiveau.statut` — ce que le programme attend d'une connaissance à un
 * niveau donné. Les trois marques de la Progression des apprentissages :
 * la flèche, l'étoile, le grisé.
 */
export const STATUTS_ATTENTE = ['en_construction', 'maitrise_attendue', 'reinvestie'];
/** Les marques telles que l'extraction du document ministériel les nomme. */
export const MARQUES_TRAJECTOIRE = ['fleche', 'etoile', 'grise'];
/** La marque du document → le statut interrogeable. Une seule table, ici. */
export const STATUT_DE_LA_MARQUE = {
    fleche: 'en_construction',
    etoile: 'maitrise_attendue',
    grise: 'reinvestie',
};
/**
 * Les années telles que la Progression des apprentissages les nomme. `6e` est
 * la dernière année du primaire : elle se lit dans la trajectoire, mais n'a
 * pas de `NiveauScolaire` — donc pas de ligne `AttenteNiveau`.
 */
export const ANNEES_TRAJECTOIRE = ['6e', '1re', '2e', '3e', '4e', '5e'];
/** L'année du document → le niveau du produit ; `null` pour le primaire. */
export const NIVEAU_DE_L_ANNEE = {
    '6e': null,
    '1re': 'sec1',
    '2e': 'sec2',
    '3e': 'sec3',
    '4e': 'sec4',
    '5e': 'sec5',
};
/** Les quatre niveaux d'exercice (MV-PRG-06). Leurs noms : `NIVEAUX_EXERCICE_NOMMES` (`manuel-exercices.ts`). */
export const NIVEAUX_EXERCICE = [1, 2, 3, 4];
/** Somme attendue des parts de rythme d'un chapitre, et sa tolérance (MV-PRG-03). */
export const TOLERANCE_PART_RYTHME = 0.001;
/** Le niveau, tel qu'on le dit à un élève ou à un tuteur : « 3e secondaire ». */
export function libelleNiveauScolaire(niveau) {
    const rang = Number(niveau.replace('sec', ''));
    return `${rang}${rang === 1 ? 're' : 'e'} secondaire`;
}
/**
 * Alias → code de TOUT ce qu'un dossier peut citer : ses notions, puis ses
 * prérequis partagés. Les erreurs (`prerequisEnCause`) et les exercices
 * (`notions`) citent des alias : c'est ici qu'ils se résolvent.
 */
export function codesParAlias(fichier) {
    return new Map([
        ...fichier.notions.map((n) => [n.alias, n.code]),
        ...(fichier.partages ?? []).map((p) => [p.alias, p.ref]),
    ]);
}
/* -------------------------------------------------------------------------- */
/* 3. Le rapport d'import (MV-CNT-02)                                         */
/* -------------------------------------------------------------------------- */
/** Ce qu'il advient d'un élément du dossier. */
export const ISSUES_IMPORT = ['cree', 'mis_a_jour', 'inchange', 'refuse'];
/** Les familles d'éléments de la partie « programme ». */
export const FAMILLES_IMPORT_PROGRAMME = [
    'attente',
    'notion',
    'notion_retiree',
    'prerequis',
    'chapitre',
];
//# sourceMappingURL=manuel-programme.js.map